import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Wallet, TrendUp, TrendDown, Target, CreditCard, ArrowUp, ArrowDown, DotsSixVertical, ListChecks, ArrowRight } from '@phosphor-icons/react';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [user, setUser] = useState(null);
  const [projection, setProjection] = useState([]);
  const [projectionData, setProjectionData] = useState(null);
  const [spendingSuggestion, setSpendingSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [filteredSummary, setFilteredSummary] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, monthlyRes, categoryRes, transactionsRes, goalsRes, userRes, projectionRes, suggestionRes, todayRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stats/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/monthly`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/categories`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/goals/with-forecast`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/projection`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/spending-suggestion`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tasks/today-summary`, { withCredentials: true })
      ]);

      setSummary(summaryRes.data);
      setMonthlyStats(monthlyRes.data);
      setCategoryStats(categoryRes.data.slice(0, 6));
      setRecentTransactions(transactionsRes.data.slice(0, 5));
      setGoals(goalsRes.data.goals.slice(0, 3));
      setUser(userRes.data);
      setProjection(projectionRes.data.projection);
      setProjectionData(projectionRes.data);
      setSpendingSuggestion(suggestionRes.data);
      setTodaySummary(todayRes.data);
      
      // Extract available months from transactions
      const months = {};
      transactionsRes.data.forEach(t => {
        try {
          const date = new Date(t.date);
          const monthKey = date.toISOString().slice(0, 7);
          const monthLabel = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
          months[monthKey] = monthLabel;
        } catch (e) {
          // Ignore invalid dates
        }
      });
      
      const sortedMonths = Object.keys(months).sort().reverse().map(key => ({
        value: key,
        label: months[key].charAt(0).toUpperCase() + months[key].slice(1)
      }));
      
      setAvailableMonths(sortedMonths);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Filter data by selected month
  useEffect(() => {
    if (selectedMonth === 'all' || !summary) {
      setFilteredSummary(summary);
      return;
    }
    
    // Calculate filtered summary for specific month
    const filtered = recentTransactions.filter(t => {
      try {
        const date = new Date(t.date);
        const monthKey = date.toISOString().slice(0, 7);
        return monthKey === selectedMonth;
      } catch {
        return false;
      }
    });
    
    const monthIncome = filtered.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const monthExpenses = filtered.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const monthBalance = monthIncome - monthExpenses;
    
    setFilteredSummary({
      ...summary,
      total_income: monthIncome,
      total_expenses: monthExpenses,
      total_balance: monthBalance,
      net_balance: monthBalance
    });
  }, [selectedMonth, summary, recentTransactions]);

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return <LoadingBar text="Carregando seus dados..." />;
  }

  const netBalance = (filteredSummary?.total_income || 0) - (filteredSummary?.total_expenses || 0);
  const savingsRate = filteredSummary?.total_income > 0 ? ((netBalance / filteredSummary.total_income) * 100) : 0;
  
  // Use spending suggestion from backend
  const spendingTip = spendingSuggestion?.message || "Carregando sugestão...";

  return (
    <div className="p-2 sm:p-4 md:p-8 space-y-6 sm:space-y-8 bg-gradient-to-br from-background via-background to-muted/20" data-testid="dashboard-page">
      {/* Header Section */}
      <div className="space-y-2 relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Visão Geral Financeira
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Acompanhe seu progresso financeiro em tempo real</p>
          </div>
          
          {/* Month Filter */}
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-full sm:w-[220px]" data-testid="dashboard-month-filter">
              <SelectValue placeholder="Filtrar por período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os períodos</SelectItem>
              {availableMonths.map(month => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Balance Card - Hero Section */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 p-4 sm:p-6 md:p-8 shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-black/10 rounded-full -ml-48 -mb-48"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Wallet size={24} weight="bold" className="text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-medium">Saldo Total</p>
                <p className="text-white/60 text-xs">Todas as contas</p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
              Atualizado agora
            </Badge>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-4">
              <div>
                <p className="text-3xl sm:text-4xl md:text-6xl font-bold text-white tabular-nums mb-2" data-testid="total-balance">
                  R$ {(filteredSummary?.total_balance || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-white/70 text-sm mb-1">
                  {selectedMonth === 'all' ? 'Saldo Total' : `Balanço em ${availableMonths.find(m => m.value === selectedMonth)?.label}`}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${netBalance >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  {netBalance >= 0 ? <ArrowUp size={16} weight="bold" className="text-white" /> : <ArrowDown size={16} weight="bold" className="text-white" />}
                  <span className="text-white font-semibold text-sm">
                    R$ {Math.abs(netBalance).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <span className="text-white/70 text-sm">balanço total</span>
              </div>
              
              {/* Variable Spending Suggestion */}
              {spendingSuggestion && (
                <div className="mt-3 p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                  <p className="text-white/80 text-xs mb-2 uppercase tracking-wide">💰 Gasto Variável Sugerido</p>
                  <p className="text-white text-2xl font-bold tabular-nums mb-2">
                    R$ {(spendingSuggestion?.available_for_variable ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-white/70 text-xs">{spendingTip}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-white/20">
              <div>
                <p className="text-white/60 text-xs mb-1">Receitas</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-white tabular-nums">R$ {(summary?.total_income || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs mb-1">Despesas</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-white tabular-nums">R$ {(summary?.total_expenses || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs mb-1">Taxa de Economia</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-white tabular-nums">{(savingsRate ?? 0).toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tarefas do Dia - Compact */}
      {todaySummary && (
        <div className="flex items-center gap-4 neo-card-sm !py-3 !px-5" data-testid="today-tasks-card">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <ListChecks size={18} weight="bold" className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[var(--neo-text-dark)]">
                {todaySummary.pending_today} tarefa{todaySummary.pending_today !== 1 ? 's' : ''} hoje
              </span>
              {todaySummary.overdue_count > 0 && (
                <span className="text-xs font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded">
                  +{todaySummary.overdue_count} atrasada{todaySummary.overdue_count > 1 ? 's' : ''}
                </span>
              )}
              {todaySummary.completed_today > 0 && (
                <span className="text-xs text-green-600 font-medium">
                  {todaySummary.completed_today} concluída{todaySummary.completed_today > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <Button size="sm" onClick={() => navigate('/tasks')} className="neo-btn text-xs shrink-0" data-testid="today-tasks-view-all">
            Ver <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}
      {/* Evolução Mensal */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg sm:text-xl md:text-2xl">Evolução Mensal</CardTitle>
              <CardDescription>Comparativo de receitas e despesas</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs text-muted-foreground">Receitas</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-xs text-muted-foreground">Despesas</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={monthlyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis 
                dataKey="month" 
                stroke="#6b7280" 
                style={{ fontSize: '12px', fontWeight: 500 }} 
                tickMargin={10}
              />
              <YAxis 
                stroke="#6b7280" 
                style={{ fontSize: '12px', fontWeight: 500 }} 
                tickMargin={10}
                tickFormatter={(value) => `R$ ${(Number(value ?? 0)/1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px',
                  padding: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
                formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, '']}
                labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
              />
              <Line 
                type="monotone" 
                dataKey="income" 
                name="Receitas" 
                stroke="#10b981" 
                strokeWidth={3} 
                dot={{ r: 4, fill: '#10b981' }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="expense" 
                name="Despesas" 
                stroke="#ef4444" 
                strokeWidth={3} 
                dot={{ r: 4, fill: '#ef4444' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-2 hover:shadow-lg transition-all duration-300 hover:border-primary/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendUp size={24} weight="bold" className="text-green-600 dark:text-green-400" />
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {selectedMonth === 'all' ? 'Total' : 'Mês'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Receitas</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 tabular-nums" data-testid="total-income">
                R$ {(filteredSummary?.total_income || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-all duration-300 hover:border-primary/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <TrendDown size={24} weight="bold" className="text-red-600 dark:text-red-400" />
              </div>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                {selectedMonth === 'all' ? 'Total' : 'Mês'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Despesas</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 tabular-nums" data-testid="total-expenses">
                R$ {(filteredSummary?.total_expenses || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-all duration-300 hover:border-primary/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Target size={24} weight="bold" className="text-blue-600 dark:text-blue-400" />
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {summary?.goals_count || 0} metas
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Investido</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 tabular-nums" data-testid="total-invested">
                R$ {(summary?.total_invested || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Goals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Transações Recentes</CardTitle>
            <CardDescription>Suas últimas movimentações</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentTransactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma transação ainda</p>
              ) : (
                recentTransactions.map(t => (
                  <div key={t.transaction_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.type === 'income' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {t.type === 'income' ? <ArrowUp size={20} weight="bold" /> : <ArrowDown size={20} weight="bold" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{t.description}</p>
                        <p className="text-xs text-muted-foreground">{t.category}</p>
                      </div>
                    </div>
                    <p className={`font-bold tabular-nums ${t.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.type === 'income' ? '+' : '-'}R$ {t.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Goals Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Progresso das Metas</CardTitle>
            <CardDescription>Acompanhe seus objetivos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {goals.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma meta definida</p>
              ) : (
                goals.map(goal => {
                  const progress = (goal.current_amount / goal.target_amount) * 100;
                  return (
                    <div key={goal.goal_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{goal.name}</p>
                          <p className="text-xs text-muted-foreground">{goal.category}</p>
                        </div>
                        <span className="text-sm font-semibold text-primary">{(isNaN(progress) ? 0 : progress).toFixed(0)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>R$ {goal.current_amount.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</span>
                        <span>R$ {goal.target_amount.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Por Categoria</CardTitle>
          <CardDescription>Distribuição de gastos</CardDescription>
        </CardHeader>
        <CardContent>
          {categoryStats.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={categoryStats}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {categoryStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {categoryStats.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                      <span className="text-muted-foreground">{cat.category}</span>
                    </div>
                    <span className="font-semibold tabular-nums">R$ {cat.amount.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-muted-foreground">
              <p className="text-center">Nenhuma despesa registrada ainda</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projection Chart - 12 Months (Last) */}
      {projectionData && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="neo-stat-card border-l-4 border-l-[var(--neo-green)]">
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--neo-text-light)] mb-1">Saldo Livre Mensal</p>
                <p className={`text-3xl font-bold tabular-nums ${(projectionData?.saldo_livre_mensal ?? 0) >= 0 ? 'text-[var(--neo-green)]' : 'text-[var(--neo-red)]'}`}>
                  R$ {(projectionData?.saldo_livre_mensal ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-[var(--neo-text-light)] mt-2">
                  = R$ {(projectionData?.total_receitas_mensais ?? 0).toFixed(2)} (receitas) - R$ {(projectionData?.total_despesas_recorrentes ?? 0).toFixed(2)} (despesas)
                </p>
                {(projectionData?.saldo_livre_mensal ?? 0) === 0 && (
                  <p className="text-xs text-[var(--neo-gold)] mt-2">
                    Adicione transações recorrentes para ver a projeção
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="neo-stat-card border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--neo-text-light)] mb-1">Acumulado em 12 Meses</p>
                <p className="text-3xl font-bold text-blue-600 tabular-nums">
                  R$ {((projectionData?.saldo_livre_mensal ?? 0) * 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-[var(--neo-text-light)] mt-2">
                  R$ {(projectionData?.saldo_livre_mensal ?? 0).toFixed(2)} x 12 meses
                </p>
              </CardContent>
            </Card>

            <Card className="neo-stat-card border-l-4 border-l-[var(--neo-gold)]">
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--neo-text-light)] mb-1">Saldo Projetado (12 meses)</p>
                <p className="text-3xl font-bold text-[var(--neo-gold)] tabular-nums">
                  R$ {(projection[11]?.total_projetado ?? (projectionData?.current_balance ?? 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-[var(--neo-text-light)] mt-2">
                  Saldo atual + acumulado
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Projeção de Saldo - Próximos 12 Meses</CardTitle>
              <CardDescription>Acúmulo mensal baseado apenas em transações recorrentes</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={projection}>
                  <defs>
                    <linearGradient id="colorAccumulated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                  <XAxis 
                    dataKey="month_label" 
                    stroke="#6b7280" 
                    style={{ fontSize: '12px', fontWeight: 500 }} 
                    tickMargin={10}
                  />
                  <YAxis 
                    stroke="#6b7280" 
                    style={{ fontSize: '12px', fontWeight: 500 }} 
                    tickMargin={10}
                    tickFormatter={(value) => `R$ ${(Number(value ?? 0)/1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '12px',
                      padding: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value, name) => [`R$ ${(value ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, name]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        return `Mês ${payload[0].payload.month_number}: ${label}`;
                      }
                      return label;
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="saldo_acumulado" 
                    name="Saldo Acumulado"
                    stroke="#10b981" 
                    strokeWidth={3} 
                    fill="url(#colorAccumulated)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Legenda:</strong> Este gráfico mostra o acúmulo progressivo do saldo livre mensal (R$ {(projectionData?.saldo_livre_mensal ?? 0).toFixed(2)}) 
                  ao longo de 12 meses. Apenas transações recorrentes são consideradas.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
